import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import {
  Client as ConversationsClient,
  Conversation,
  Message
} from '@twilio/conversations';
import * as ChatActions from './chat.actions';
import { ChatConvo } from '@care-portals/api-interfaces';

interface Device {
  deviceId: string;
  // track: LocalVideoTrack | LocalAudioTrack;
  isStreaming: boolean;
}
@Injectable({
  providedIn: 'root'
})
export class ChatService {
  // private _room: Room;
  private _conversationsClient: ConversationsClient;
  private chatProxies = {}; // saving this in the ngrc state store throws error; so it's gonna live here
  private isMobile: boolean = false;
  private tokenRefreshTimer;
  // remoteTracks: {[key: string]: RemoteTrackPublication} = {};

  // get room() {
  //   return this._room;
  // }

  async removeParticipant(identity: string, chatId: string) {
    const chat = await this.getChat(chatId);
    if (!chat) return;
    const participant = await chat.getParticipantByIdentity(identity);
    if (!participant) return;
    participant.remove();
  }

  selectedDevices: { audio: Device; video: Device } = {
    audio: { deviceId: null, isStreaming: false },
    video: { deviceId: null, isStreaming: false }
  };

  // connectOptions: ConnectOptions = {
  //   bandwidthProfile: {
  //     video: {
  //       //     dominantSpeakerPriority: 'high',
  //       //     mode: 'collaboration',
  //       //     clientTrackSwitchOffControl: 'auto',
  //       //     contentPreferencesMode: 'auto'
  //     }
  //   },
  //   // dominantSpeaker: false,
  //   // preferredVideoCodecs: [{ codec: 'VP8', simulcast: true }],

  //   // Comment this line if you are playing music.
  //   maxAudioBitrate: 16000,

  //   audio: {},
  //   // Capture 720p video @ 24 fps.
  //   video: { height: 720, frameRate: 24, width: 1280, deviceId: {} }
  // };

  constructor(private readonly store: Store) {
    this.isMobile =
      typeof navigator === 'undefined' ||
      typeof navigator.userAgent !== 'string'
        ? false
        : /Mobile/.test(navigator.userAgent);
    // if (this.isMobile) {
    //   this.connectOptions.bandwidthProfile.video.maxSubscriptionBitrate = 2500000;
    // }

    // set devices object from localstorage if not defined
    // this.setAudioDevice(this.audioDeviceId);
    // this.setVideoDevice(this.videoDeviceId);
  }

  initClient(token: string) {
    if (!!this._conversationsClient) {
      return;
    }

    this._conversationsClient = new ConversationsClient(token, {});

    this._conversationsClient.on('connectionStateChanged', async state => {
      this.store.dispatch(
        ChatActions.connectionStateChanged({ connectionState: state as any })
      );
    });

    this._conversationsClient.on(
      'conversationJoined',
      async (conversation: Conversation) => {
        const participants = await (
          await conversation.getParticipants()
        ).map(p => p.identity);
        this.store.dispatch(
          ChatActions.chatJoined({
            chatId: conversation.sid,
            participants: { ...participants }
          })
        );
      }
    );

    this._conversationsClient.on('conversationLeft', async conversation => {
      const participants = await (
        await conversation.getParticipants()
      ).map(p => p.identity);
      this.store.dispatch(
        ChatActions.chatLeft({ participants: { ...participants } })
      );
    });

    // refresh token every 5 minutes
    this.tokenRefreshTimer = setInterval(
      () => this.store.dispatch(ChatActions.refreshToken()),
      300000
    );
    return !!this._conversationsClient;
  }

  async closeConnection() {
    clearInterval(this.tokenRefreshTimer);
    // const chat = await this.getChat(chatId)
    // chat.removeAllListeners()
    this.chatProxies = {};
    // this.room?.localParticipant?.videoTracks.forEach(t => {
    //   t.unpublish();
    //   t.track.stop();
    // });
    this._conversationsClient?.shutdown();
    this._conversationsClient = null;
  }

  async getChat(chatId: string): Promise<ChatConvo> {
    if (!this._conversationsClient || !chatId) {
      console.error('getChat Failed', {
        chatId,
        client: this._conversationsClient
      });
      return null;
    }
    let chatProxy = this.chatProxies[chatId];
    if (!chatProxy) {
      chatProxy = await this._conversationsClient.getConversationBySid(chatId);
      this.chatProxies[chatId] = chatProxy;
    }
    return chatProxy;
  }

  async setNewToken(token: string) {
    this._conversationsClient = await this._conversationsClient.updateToken(
      token
    );
  }

  // load initial batch and watch for new messages
  async loadMessages(
    chatId,
    { pageSize = 30, anchor = undefined, direction = undefined } = {}
  ) {
    const chat = await this.getChat(chatId);
    const count = await chat.getMessagesCount();
    this.store.dispatch(ChatActions.updateChatCount({ chatId, count }));
    if (anchor > count - 1) return [];
    const messages = await chat.getMessages(
      pageSize,
      anchor ? count - anchor - 1 : undefined,
      direction
    );

    const mappedMsgs = (
      await Promise.all(messages.items.map(i => this.parseMessage(i)))
    ).filter(m => m.type != 'videoInvite');

    chat.on('messageAdded', async m => {
      const parsedMsg = await this.parseMessage(m);
      this.store.dispatch(
        ChatActions.messageAdded({
          chatId: m.conversation.sid,
          message: parsedMsg
        })
      );
    });
    return mappedMsgs;
  }

  private async parseMessage(msg: Message) {
    const files = [];
    if (msg.attachedMedia?.length) {
      const promises = msg.attachedMedia.map(m => m.getContentTemporaryUrl());
      const urls = await Promise.all(promises);
      urls.forEach((url, i) => {
        files.push({
          icon: 'file-text-outline',
          url,
          type: msg.attachedMedia[i].contentType
        });
      });
    }

    if (msg.body?.indexOf('JSON::') === 0) {
      const jsonMsg = JSON.parse(msg.body.replace('JSON::', ''));
      return {
        ...jsonMsg,
        date: msg.dateCreated,
        author: msg.author
      };
    } else {
      return {
        body: msg.body,
        author: msg.author,
        dateCreated: msg.dateCreated,
        files,
        type: files.length ? 'file' : 'text'
      };
    }
  }

  async sendMessage(chatId: string, message: string, files?: File[]) {
    const chat = await this.getChat(chatId);
    const promises = [];
    if (message) {
      promises.push(chat.sendMessage(message));
    }
    (files || []).forEach(file => {
      promises.push(chat.sendMessage({ contentType: file.type, media: file }));
    });
    return await Promise.all(promises);
  }

  // VIDEO

  /**
   * Get the list of input devices of a given kind.
   * @param kind - 'audio' | 'video'
   * @returns {Promise<MediaDeviceInfo[]>} the list of media devices
   */
  // async getInputDevices(kind) {
  //   try {
  //     const media = await navigator.mediaDevices?.getUserMedia({
  //       audio: kind === 'audio',
  //       video: kind === 'video'
  //     });
  //     media.getTracks().forEach(t => t.stop());
  //     const devices = await navigator.mediaDevices.enumerateDevices();
  //     return devices.filter(device => device.kind === `${kind}input`);
  //   } catch (error) {
  //     // Handle the error
  //     if (
  //       error.name === 'NotFoundError' ||
  //       error.name === 'DevicesNotFoundError'
  //     ) {
  //       console.error('Requested device not found:', error.message);
  //     } else {
  //       console.error('Error accessing media device:', error.message);
  //     }
  //   }
  // }

  // async joinVideoRoom(auth: { token: string; expiry: number }, chatId) {
  //   this._room = await connect(auth.token, {
  //     name: chatId,
  //     ...this.connectOptions
  //   } as ConnectOptions);
  //   this.stopAudio();
  //   this.stopVideo();
  //   console.log('joinVideoRoom >>', this.room);
  //   return this.room.sid;
  // }

  // async leaveRoom() {
  //   console.log('leaveRoom', this.room);
  //   this.room?.localParticipant?.videoTracks.forEach(t => {
  //     t.track.stop();
  //     t.unpublish();
  //   });
  //   this.room?.localParticipant?.audioTracks.forEach(t => {
  //     t.track.stop();
  //     t.unpublish();
  //   });
  //   this.room?.disconnect();
  //   delete this._room;
  // }

  // async setVideoDevice(deviceId) {
  //   // const [track] = await createLocalTracks({ video: { deviceId } });
  //   localStorage.setItem('care_portals.videoDeviceId', deviceId);
  //   // Stop the previous LocalTrack, if present.
  //   if (this.selectedDevices.video.isStreaming) {
  //     this.stopVideo();
  //   }
  //   // this.selectedDevices.video.track = track as LocalVideoTrack;
  //   this.selectedDevices.video.deviceId = deviceId;
  //   this.selectedDevices.video.isStreaming = false;
  // }

  // get videoDeviceId() {
  //   return (
  //     this.selectedDevices.video.deviceId ||
  //     localStorage.getItem('care_portals.videoDeviceId')
  //   );
  // }

  // async stopVideo() {
  //   this.selectedDevices.video.isStreaming = false;
  //   this.room?.localParticipant?.videoTracks.forEach(t => {
  //     t.track.stop();
  //     t.unpublish();
  //   });
  // }

  // async startVideo() {
  //   console.log('CREATING LOCAL TRACK');
  //   const track = await createLocalTracks({
  //     video: { deviceId: this.videoDeviceId }
  //   });
  //   this.room?.localParticipant.publishTrack(track[0]);
  //   this.selectedDevices.video.isStreaming = true;
  //   return track[0] as LocalVideoTrack; // need to return the track to the component to attach to localVideo
  // }

  // AUDIO FUNCTIONS

  // async setAudioDevice(deviceId) {
  //   if (!deviceId) return;

  //   if (this.selectedDevices.audio.isStreaming) {
  //     this.stopAudio();
  //   }
  //   localStorage.setItem('care_portals.audioDeviceId', deviceId);
  //   this.selectedDevices.audio.deviceId = deviceId;
  // }

  // get audioDeviceId() {
  //   return (
  //     this.selectedDevices.audio.deviceId ||
  //     localStorage.getItem('care_portals.audioDeviceId')
  //   );
  // }

  // async stopAudio() {
  //   // this.selectedDevices?.audio?.track?.stop();
  //   // this.room.localParticipant.unpublishTrack(this.selectedDevices?.audio?.track);
  //   this.room.localParticipant.audioTracks.forEach(t => {
  //     t.track.stop();
  //     t.unpublish();
  //   });
  //   this.selectedDevices.audio.isStreaming = false;
  // }

  // async startAudio() {
  //   const publishableTrack = await createLocalTracks({
  //     audio: { deviceId: this.audioDeviceId }
  //   });
  //   this.room?.localParticipant.publishTrack(publishableTrack[0]);
  //   this.selectedDevices.audio.isStreaming = true;
  // }
}
